import { useId, useMemo } from "react";
import { Img } from "react-image";

import { useDocumentModal } from "../../../../common/CustomModal/CustomModalContext";
import { api_without_cancellation } from "../../../../../api/api";
import { getCaseId, getClientId } from "../../../../../Utils/helper";
import {
  BrokenPreviewImage,
  photoName,
} from "../../../../Photo/NewPhotoPage/components/photo-view-card";
import mixColorWithWhite from "../../../../TreatmentPage/utils/helperFn";
import "../../../../common/defaultPhotoSlot.css";

const PhotoPlaceholderIcon = () => (
  <svg
    className="cursor-pointer m-t-10"
    width="50"
    height="50"
    style={{ flexShrink: 0 }}
    viewBox="0 0 500 500"
    aria-hidden="true"
    focusable="false"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M386.9,401.1L113.1,401.1C67.8,401.1 31,364.3 31,319C31,282.2 55.4,250.3 90.2,240.3C98.5,217.8 117.9,201.5 141.2,197.1C147.4,142.6 193.8,100.1 250,100.1C294.7,100.1 335,127.8 351.5,168.7C399.6,171.5 438.2,210 441.4,258C459,273.6 469,295.5 469,319.1C469,364.2 432.2,401.1 386.9,401.1Z"
      fill="var(--primary-25)"
    />
    <path
      d="M386.9,401.1L113.1,401.1C67.8,401.1 31,364.3 31,319C31,282.2 55.4,250.3 90.2,240.3C98.5,217.8 117.9,201.5 141.2,197.1C147.4,142.6 193.8,100.1 250,100.1C294.7,100.1 335,127.8 351.5,168.7C399.6,171.5 438.2,210 441.4,258C459,273.6 469,295.5 469,319.1C469,364.2 432.2,401.1 386.9,401.1ZM154.2,223.1C135.1,223.1 118.7,236.1 114.3,254.8L112.3,263.6L103.4,265.2C77.3,269.9 58.4,292.5 58.4,318.9C58.4,349.1 83,373.7 113.2,373.7L387,373.7C417.2,373.7 441.8,349.1 441.8,318.9C441.8,301.8 433.8,285.9 419.9,275.4L414.5,271.3L414.5,263.4C414.5,226.4 383.8,195.7 346,195.7L332,196.4L328.9,186.1C318.7,151.5 286.3,127.3 250.2,127.3C204.9,127.3 168.1,164.1 168.1,209.4L168.1,223.1L154.2,223.1Z"
      fill="var(--primary-25)"
    />
    <g fill="var(--primary-40)">
      <rect
        x="236.3"
        y="229"
        width="27.4"
        height="117.3"
        transform="matrix(1.85095,0,0,1.675,-212.907,-159.193)"
      />
      <path
        d="M295.1,287.6L250,242.5L204.9,287.6L185.6,268.2L250,203.8L314.4,268.2L295.1,287.6Z"
        transform="matrix(1.71175,0,0,1.71175,-177.938,-187.823)"
      />
    </g>
  </svg>
);

const CARD_WIDTH = 255;
const GAP = 5;

const isPagePhotoSlot = (photoSlot) =>
  photoSlot === null ||
  photoSlot === undefined ||
  photoSlot === 0 ||
  photoSlot.slot_number === 0 ||
  photoSlot.slot_number === null;

const PhotoTabComponent = ({ data, refetchData, tabData, currentPageId }) => {
  const uploadIdPrefix = useId();
  const { showDocumentModal } = useDocumentModal();
  const origin = process.env.REACT_APP_BACKEND_URL || "https://dev.simplefirm.com";

  const slots = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    const allSlots = [];

    // Process each section
    data.forEach((section) => {
      if (
        currentPageId !== null &&
        currentPageId !== undefined &&
        String(section.page?.id) !== String(currentPageId)
      )
        return;
      const sectionName = section.page?.name || "Unsorted";
      const isPanelsEnabled = section.page?.panels || false;
      const sectionIcon =
        section.page?.page_icon ||
        "https://simplefirm-bucket.s3.amazonaws.com/static/images/documents-icon-color_b6TvcB7.svg";

      // Get the page slots template
      const pageSlots = section.page_slots || [];
      const pageUploadSlot = {
        slotInfo: pageSlots.find(isPagePhotoSlot) || null,
        page: section.page,
        sectionName,
        sectionIcon,
        isPanels: false,
        panelName: null,
        panelId: null,
        specialty: null,
        photo: null,
        isAttached: false,
      };

      // Keep the general upload card available alongside every page photo.
      allSlots.push(pageUploadSlot);
      section.page_docs?.forEach((photo) => {
        if (isPagePhotoSlot(photo.photo_slot)) {
          allSlots.push({
            ...pageUploadSlot,
            photo,
            isAttached: true,
          });
        }
      });

      const numberedSlots = pageSlots.filter((slotInfo) => !isPagePhotoSlot(slotInfo));

      if (isPanelsEnabled && section.panels && section.panels.length > 0) {
        section.panels.forEach((panel) => {
          const panelSlots = panel.slots?.length
            ? panel.slots.filter((slotInfo) => !isPagePhotoSlot(slotInfo))
            : numberedSlots.map((slotInfo) => ({ ...slotInfo, panel_id: panel.id }));

          panelSlots.forEach((slotInfo) => {
            const slot = {
              slotInfo,
              pageUploadSlot,
              sectionName,
              sectionIcon,
              isPanels: true,
              panelName: panel.panel_name || null,
              panelId: panel.id || null,
              specialty: panel.specialty || null,
              photo: null,
              isAttached: false,
            };

            const matchingPhoto = panel.photos?.find(
              (photo) => photo.photo_slot?.id === slotInfo.id
            );
            if (matchingPhoto) {
              slot.photo = matchingPhoto;
              slot.isAttached = true;
            }

            allSlots.push(slot);
          });
        });
      } else {
        numberedSlots.forEach((slotInfo) => {
          const slot = {
            slotInfo,
            pageUploadSlot,
            sectionName,
            sectionIcon,
            isPanels: false,
            panelName: null,
            panelId: null,
            specialty: null,
            photo: null,
            isAttached: false,
          };

          const matchingDoc = section.page_docs?.find(
            (doc) => !isPagePhotoSlot(doc.photo_slot) && doc.photo_slot?.id === slotInfo.id
          );
          if (matchingDoc) {
            slot.photo = matchingDoc;
            slot.isAttached = true;
          }

          allSlots.push(slot);
        });
      }
    });
    return allSlots;
  }, [data, currentPageId]);

  const handleFileChange = async (event, slot) => {
    const input = event.target;
    const files = Array.from(input.files || []);
    const isPageUpload = isPagePhotoSlot(slot.slotInfo);
    let shouldRefetch = false;

    for (const [index, file] of files.entries()) {
      const uploadSlot = index === 0 || isPageUpload ? slot : slot.pageUploadSlot;
      const formData = new FormData();
      formData.append("file", file);
      try {
        const response = await api_without_cancellation.post(
          `${origin}/api/upload_photo/${getClientId()}/${getCaseId()}/`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const result = await response.data;

        const pageNameToModelName = {
          treatment: "CaseProviders",
          defendants: "Defendant",
          witnesses: "Witness",
          insurance: "Insurance",
          parties: "OtherParty",
          reports: "Reports",
          employment: "Employment",
          loans: "CaseLoan",
          discovery: "Discovery",
          recording: "Recordings",
          experts: "CaseExpert",
          Litigation: "LitigationAct",
        };

        const page = uploadSlot.page || uploadSlot.slotInfo?.page;
        const modelName = uploadSlot.isPanels ? pageNameToModelName[page?.name?.toLowerCase()] : "";

        const res = await api_without_cancellation.post("/api/attach_photo_to_page/", {
          page_id: page?.id ?? page,
          case_id: getCaseId(),
          slot_id: uploadSlot.slotInfo?.id ?? null,
          photo_id: result?.doc?.id,
          panel_id: uploadSlot.panelId,
          model_name: modelName ? modelName : "",
        });
        if (res.status === 200 || res.status === 201) {
          shouldRefetch = true;
        }
      } catch (error) {
        console.error("Upload failed:", error);
      }
    }

    input.value = "";
    if (shouldRefetch) {
      await refetchData();
    }
  };

  const handleDocPreview = (photo) => {
    showDocumentModal("photo", photo.image, photo, tabData, refetchData, photo.case_thumbnail);
  };

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: GAP,
        width: "100%",
        minWidth: 0,
        marginBottom: GAP,
      }}
    >
      {slots.map((slot, index) => {
        const sectionName = slot.sectionName;
        const isPagePhoto = isPagePhotoSlot(slot.slotInfo);
        const cardKey = `${sectionName}-${slot.panelId ?? "page"}-${slot.slotInfo?.id ?? "general"}-${slot.photo?.id ?? "upload"}`;
        const cardStyle = {
          flex: `0 0 ${CARD_WIDTH}px`,
          width: CARD_WIDTH,
          minWidth: CARD_WIDTH,
          maxWidth: CARD_WIDTH,
        };
        if (slot?.isAttached === true) {
          return (
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              key={cardKey}
              style={cardStyle}
            >
              {sectionName === "Treatment" && (
                <div
                  className="has-primary-font-detail-view-card"
                  style={{
                    display: "flex",
                    justifyContent:
                      slot?.panelName === null
                        ? isPagePhoto
                          ? "center"
                          : "flex-start"
                        : "flex-start",
                    alignItems: "center",
                    // padding: "0px 10px",
                    background: slot?.specialty?.color
                      ? mixColorWithWhite(slot?.specialty?.color, 10)
                      : "var(--primary-15)",
                    color: "var(--primary)",
                    width: "100%",
                    minWidth: 0,
                    maxWidth: "100%",
                    fontSize: "14px",
                    fontWeight: "600",
                    height: "25px",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {slot?.panelName !== null && (
                    <>
                      <span
                        className="d-flex m-r-5 align-items-center justify-content-center"
                        style={{
                          width: "25px",
                          height: "25px",
                          background: slot?.specialty?.color || "#000", // fallback color
                          color: "white",
                          minWidth: "25px",
                        }}
                      >
                        {slot?.specialty?.name?.[0] || "?"}
                      </span>
                      <span
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "14px",
                          fontWeight: "600",
                          height: "25px",
                          textTransform: "uppercase",
                          alignContent: "center",
                        }}
                      >
                        {slot?.panelName}
                      </span>
                    </>
                  )}

                  {slot?.panelName === null && (
                    <>
                      {!isPagePhoto && (
                        <span
                          className="d-flex m-r-5 align-items-center justify-content-center"
                          style={{
                            width: "25px",
                            height: "25px",
                            background: slot?.specialty?.color || "var(--primary)", // fallback color
                            color: "white",
                            minWidth: "25px",
                          }}
                        >
                          {slot?.specialty?.name?.[0] || "?"}
                        </span>
                      )}
                      <span
                        className="m-l-2"
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "14px",
                          fontWeight: "600",
                          height: "25px",
                          textTransform: "uppercase",
                          alignContent: "center",
                        }}
                      >
                        {isPagePhoto
                          ? `Attached to ${sectionName} Page`
                          : slot?.slotInfo?.slot_name}
                      </span>
                    </>
                  )}
                </div>
              )}
              {sectionName !== "Treatment" && (
                <div
                  className="has-primary-font-detail-view-card"
                  style={{
                    display: !isPagePhoto && "flex",
                    justifyContent: !isPagePhoto && "center",
                    alignItems: !isPagePhoto && "center",
                    alignContent: isPagePhoto && "center",
                    padding: "0px 10px",
                    background: "var(--primary-15)",
                    color: "var(--primary)",
                    width: "100%",
                    minWidth: 0,
                    maxWidth: "100%",
                    fontSize: "14px",
                    fontWeight: "600",
                    height: "25px",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {sectionName
                    ? isPagePhoto
                      ? `Attached to ${sectionName} Page`
                      : slot?.panelName ||
                        (slot?.slotInfo?.slot_name === null && "Available") ||
                        slot?.slotInfo?.slot_name ||
                        "Unattached Photo"
                    : "Unattached Photo"}
                  {/* {sectionName
                                      ? slot.panelName ||
                                        (slot?.slotInfo?.slot_number === 0 &&
                                          slot?.slotInfo?.slot_name) ||
                                        (slot?.slotInfo?.slot_name === null &&
                                          "Available") ||
                                        slot?.slotInfo?.slot_name ||
                                        "Unattached Photo"
                                      : "Unattached Photo"} */}
                </div>
              )}
              <div
                className={"font-weight-semibold cursor-pointer p-0 detailed-empty-card"}
                id="no-vertical-border"
                onClick={() => handleDocPreview(slot?.photo)}
                key={slot?.photo.id}
                style={{
                  width: "100%",
                  minWidth: 0,
                  maxWidth: "100%",
                  minHeight: "322px",
                  height: "322px",
                  border: "none",
                }}
              >
                <div
                  title={slot?.photo.title || slot?.slotInfo?.slot_name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "0 5px",
                    // height: "50px",
                    // background: "red",
                  }}
                >
                  <span
                    className=""
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      // marginRight: "5px",
                      // marginTop: "2px",
                    }}
                  >
                    {/* <i className="ic ic-19 ic-file-colored cursor-pointer img-19px"></i> */}
                    <img
                      src="https://simplefirm-bucket.s3.amazonaws.com/static/images/photo-icon-color_2CyRX1a_GgdCtlG.svg"
                      className="ic ic-19 cursor-pointer img-19px"
                    />
                  </span>
                  <p
                    className="name new-view-title p-0"
                    style={{
                      alignContent: "center",
                    }}
                  >
                    <span className="text-primary-25">
                      {isPagePhoto ? "" : `${slot?.slotInfo?.slot_number}.`}{" "}
                    </span>
                    {photoName(slot?.photo?.title, slot?.slotInfo?.slot_name)}
                  </p>
                </div>
                <div
                  id={`${slot?.photo.id}-${slot?.photo.title || slot?.slotInfo?.slot_name}`}
                  className="detailed-empty-card-img-div"
                  style={{
                    height: "297px",
                    // background: "green",
                  }}
                >
                  {slot?.photo?.image ? (
                    <Img
                      src={slot?.photo?.image}
                      alt={slot?.photo?.title || slot?.slotInfo?.slot_name || "Photo"}
                      unloader={<BrokenPreviewImage />}
                      style={{
                        height: "100%",
                        width: "100%",
                        maxWidth: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <BrokenPreviewImage />
                  )}
                </div>
              </div>
            </div>
          );
        } else {
          return (
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              key={cardKey}
              style={cardStyle}
            >
              {sectionName === "Treatment" && (
                <div
                  className="has-primary-font-detail-view-card"
                  style={{
                    display: "flex",
                    justifyContent:
                      slot?.panelName === null
                        ? isPagePhoto
                          ? "center"
                          : "flex-start"
                        : "flex-start",
                    alignItems: "center",
                    // padding: "0px 10px",
                    background: slot?.specialty?.color
                      ? mixColorWithWhite(slot?.specialty?.color, 10)
                      : "var(--primary-15)",
                    color: "var(--primary)",
                    width: "100%",
                    minWidth: 0,
                    maxWidth: "100%",
                    fontSize: "14px",
                    fontWeight: "600",
                    height: "25px",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {slot?.panelName !== null && (
                    <>
                      <span
                        className="d-flex m-r-5 align-items-center justify-content-center"
                        style={{
                          width: "25px",
                          height: "25px",
                          background: slot?.specialty?.color || "#000", // fallback color
                          color: "white",
                          minWidth: "25px",
                        }}
                      >
                        {slot?.specialty?.name?.[0] || "?"}
                      </span>
                      <span
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "14px",
                          fontWeight: "600",
                          height: "25px",
                          textTransform: "uppercase",
                          alignContent: "center",
                        }}
                      >
                        {slot?.panelName}
                      </span>
                    </>
                  )}

                  {slot?.panelName === null && (
                    <>
                      {!isPagePhoto && (
                        <span
                          className="d-flex m-r-5 align-items-center justify-content-center"
                          style={{
                            width: "25px",
                            height: "25px",
                            background: slot?.specialty?.color || "var(--primary)", // fallback color
                            color: "white",
                            minWidth: "25px",
                          }}
                        >
                          {slot?.specialty?.name?.[0] || ""}
                        </span>
                      )}
                      <span
                        className="m-l-2"
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "14px",
                          fontWeight: "600",
                          height: "25px",
                          textTransform: "uppercase",
                          alignContent: "center",
                        }}
                      >
                        {isPagePhoto ? `Upload to ${sectionName} Page` : slot?.slotInfo?.slot_name}
                      </span>
                    </>
                  )}
                </div>
              )}
              {sectionName !== "Treatment" && (
                <div
                  className="has-primary-font-detail-view-card"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "0px 10px",
                    background: "var(--primary-15)",
                    color: "var(--primary)",
                    width: "100%",
                    minWidth: 0,
                    maxWidth: "100%",
                    fontSize: "14px",
                    fontWeight: "600",
                    height: "25px",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {sectionName
                    ? isPagePhoto
                      ? `Upload to ${sectionName} Page`
                      : slot.panelName ||
                        (slot?.slotInfo?.slot_name === null && "Available") ||
                        slot?.slotInfo?.slot_name ||
                        "Unattached Photo"
                    : "Unattached Photo"}
                </div>
              )}

              <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{
                  width: "100%",
                  height: "322px",
                  cursor: "pointer",
                  background: "var(--primary-4)",
                }}
                onClick={() =>
                  document.getElementById(`file-upload-${uploadIdPrefix}-${index}`)?.click()
                }
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  return handleFileChange({ target: { files: e.dataTransfer.files } }, slot);
                }}
              >
                <PhotoPlaceholderIcon />
                <p className="photo-slot-upload-text text-primary-25 text-center m-t-5">
                  Drag a Photo or Click to Upload Directly
                </p>
                <input
                  type="file"
                  accept="image/*"
                  multiple={isPagePhoto}
                  aria-label={
                    isPagePhoto
                      ? `Upload photos to ${sectionName} Page`
                      : `Upload photo to ${slot.slotInfo?.slot_name || sectionName}`
                  }
                  id={`file-upload-${uploadIdPrefix}-${index}`} // unique ID
                  style={{ display: "none" }}
                  onChange={(e) => handleFileChange(e, slot)}
                />
              </div>
            </div>
          );
        }
      })}
    </div>
  );
};

export default PhotoTabComponent;
